import React from "react";

import {
  CalendarRange,
  CheckCircle2,
  CircleDollarSign,
} from "lucide-react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  formatSubscriptionDate,
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

function ForecastKpi({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-2 text-2xl font-black text-slate-950 dark:text-white">
            {value}
          </p>

          <p className="mt-2 text-xs text-slate-400">
            {description}
          </p>
        </div>

        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300">
          <Icon
            size={20}
          />
        </div>
      </div>
    </article>
  );
}

export default function SubscriptionForecastView({
  forecast = {},
  timeline = [],
  isLoading = false,
  onEdit,
}) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map(
            (item) => (
              <div
                key={item}
                className="h-36 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800"
              />
            ),
          )}
        </div>

        <div className="h-[450px] animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
      </div>
    );
  }

  const potential =
    Number(
      forecast.totalPotentialRevenue ||
        0,
    );

  const accepted =
    Number(
      forecast.totalAcceptedRevenue ||
        0,
    );

  const remaining =
    Math.max(
      potential - accepted,
      0,
    );

  const chartData =
    (forecast.months || []).map(
      (month) => ({
        ...month,

        accepted:
          Number(
            month.accepted_revenue ||
              0,
          ),

        remaining:
          Math.max(
            Number(
              month.potential_revenue ||
                0,
            ) -
              Number(
                month.accepted_revenue ||
                  0,
              ),
            0,
          ),
      }),
    );

  return (
    <section className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-500">
          Prévisions
        </p>

        <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
          Projection sur 12 mois
        </h2>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Les projections supposent
          que les clients renouvellent
          leur abonnement au tarif
          actuellement enregistré.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <ForecastKpi
          label="Potentiel 12 mois"
          value={formatSubscriptionMoney(
            potential,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
          description="Total des renouvellements attendus."
          icon={
            CircleDollarSign
          }
        />

        <ForecastKpi
          label="Déjà acceptés"
          value={formatSubscriptionMoney(
            accepted,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
          description="Renouvellements dont l’accord est déjà obtenu."
          icon={CheckCircle2}
        />

        <ForecastKpi
          label="Encore à sécuriser"
          value={formatSubscriptionMoney(
            remaining,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
          description="Potentiel restant à convertir."
          icon={CalendarRange}
        />
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 p-5 dark:border-slate-800 sm:p-6">
          <h3 className="text-lg font-black text-slate-950 dark:text-white">
            Revenus attendus par
            mois
          </h3>

          <p className="mt-1 text-sm text-slate-400">
            Part déjà acceptée et
            potentiel restant.
          </p>
        </div>

        <div className="h-[430px] p-4 sm:p-6">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 20,
                left: 10,
                bottom: 5,
              }}
            >
              <CartesianGrid
                vertical={false}
                strokeDasharray="4 4"
                strokeOpacity={
                  0.35
                }
              />

              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                width={75}
                tick={{
                  fill: "#64748b",
                  fontSize: 12,
                }}
                tickFormatter={(
                  value,
                ) =>
                  `${Number(
                    value || 0,
                  ).toLocaleString(
                    "fr-FR",
                  )} €`
                }
              />

              <Tooltip
                formatter={(
                  value,
                  name,
                ) => [
                  formatSubscriptionMoney(
                    value,
                  ),
                  name,
                ]}
              />

              <Legend />

              <Bar
                stackId="forecast"
                dataKey="accepted"
                name="Accepté"
                fill="#10b981"
                maxBarSize={45}
              />

              <Bar
                stackId="forecast"
                dataKey="remaining"
                name="À sécuriser"
                fill="#8b5cf6"
                radius={[
                  8,
                  8,
                  0,
                  0,
                ]}
                maxBarSize={45}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
        <h3 className="text-lg font-black text-slate-950 dark:text-white">
          Échéancier des prochains
          mois
        </h3>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-[850px] w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left dark:border-slate-800">
                <th className="pb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Mois
                </th>

                <th className="pb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Renouvellements
                </th>

                <th className="pb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Montant
                </th>

                <th className="pb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Principales
                  échéances
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {timeline.map(
                (month) => (
                  <tr
                    key={
                      month.key
                    }
                  >
                    <td className="py-4 font-black text-slate-950 dark:text-white">
                      {
                        month.label
                      }
                    </td>

                    <td className="py-4 text-sm font-bold text-slate-600 dark:text-slate-300">
                      {
                        month.count
                      }
                    </td>

                    <td className="py-4 font-black text-indigo-600 dark:text-indigo-300">
                      {formatSubscriptionMoney(
                        month.amount,
                      )}
                    </td>

                    <td className="py-4">
                      <div className="flex flex-wrap gap-2">
                        {month.items
                          ?.slice(
                            0,
                            4,
                          )
                          .map(
                            (
                              subscription,
                            ) => (
                              <button
                                key={
                                  subscription.id
                                }
                                type="button"
                                onClick={() =>
                                  onEdit?.(
                                    subscription,
                                  )
                                }
                                className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 dark:bg-slate-800 dark:text-slate-300"
                              >
                                {getAssociationName(
                                  subscription,
                                )}{" "}
                                ·{" "}
                                {formatSubscriptionDate(
                                  subscription.end_date,
                                  "—",
                                )}
                              </button>
                            ),
                          )}
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}